import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Navbar";
import { subscribeNewsletter } from "../lib/api";
import { OFFICES, BRAND, CONTACT } from "../lib/data";

const COLS = [
  { title: "Company", links: [["About", "/about"], ["Founder's Column", "/founders-column"], ["Insights", "/insights"], ["Contact", "/contact"]] },
  { title: "Capabilities", links: [["Services", "/services"], ["Sectors", "/industries"], ["Clients", "/case-studies"], ["Insights", "/insights"]] },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation();
  const p1 = pathname === "/" || pathname === "/about";
  const offices = p1 ? OFFICES.filter((o) => o.city === "Bengaluru") : OFFICES;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're subscribed. Welcome to the Zenspark brief.");
      setEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden bg-navy text-white grain">
      <div className="container-x relative py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo light />
            <p className="mt-6 max-w-sm text-balance text-lg font-light leading-relaxed text-white/70" data-testid="footer-descriptor">
              {p1
                ? "Better technology decisions. Accountable delivery."
                : `${BRAND.tagline} An AI-native engineering and global delivery partner, built in Bengaluru for global businesses.`}
            </p>
            <form onSubmit={submit} className="mt-8 max-w-md" data-testid="newsletter-form">
              <label className="overline text-white/50">Subscribe to the brief</label>
              <div className="mt-3 flex items-center gap-2 rounded-full border border-white/15 bg-white/5 p-1.5 pl-5">
                <input
                  data-testid="newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  disabled={loading}
                  className="grid h-9 w-9 place-items-center rounded-full bg-accent text-white transition-transform hover:scale-105 disabled:opacity-60"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          {COLS.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <h4 className="overline text-white/50">{c.title}</h4>
              <ul className="mt-5 space-y-3">
                {c.links.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-white/70 transition-colors hover:text-accent">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-3">
            <h4 className="overline text-white/50">Reach us</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-accent" /> {CONTACT.email}</li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-accent" /> {CONTACT.phone}</li>
              <li className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-accent" /> {CONTACT.location}</li>
              <li>
                <a href="https://www.linkedin.com/company/zenspark-global-corporation-pvt-ltd" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-white/70 transition-colors hover:text-accent">
                  <Linkedin className="h-4 w-4" /> Follow on LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-x-8 gap-y-2 border-t border-white/10 pt-8" data-testid="footer-offices">
          {offices.map((o) => (
            <span key={o.city} className="font-mono text-[11px] uppercase tracking-widest text-white/40">
              {o.city}, {o.country}
            </span>
          ))}
        </div>
        <div className="mt-8 flex flex-col items-start justify-between gap-3 text-xs text-white/40 sm:flex-row">
          <p data-testid="footer-legal">
            {p1
              ? "© 2026 Zenspark Global Corporation Pvt Ltd. All rights reserved."
              : `© ${new Date().getFullYear()} Zenspark Global Corporation Pvt. Ltd. All rights reserved.`}
          </p>
          <p>{BRAND.tagline}</p>
        </div>
      </div>
    </footer>
  );
};
